import React, { useState } from "react";
import DashboardLayout from "@/components/lingo/DashboardLayout";
import AICoachFloating from "@/components/lingo/AICoachFloating";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Sparkles, Send } from "lucide-react";
import { toast } from "sonner";

const SAMPLE_PROMPT =
  "Some people believe that technology has made students less creative. To what extent do you agree or disagree? Give reasons and examples. Write at least 250 words.";

export default function WritingEvaluator() {
  const { user } = useAuth();
  const [prompt, setPrompt] = useState(SAMPLE_PROMPT);
  const [answer, setAnswer] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [examType, setExamType] = useState(user?.test_type || "IELTS_ACADEMIC");
  const [taskType, setTaskType] = useState("TASK2");

  const submit = async () => {
    if (answer.trim().length < 40) return toast.error("Write at least a paragraph first");
    setLoading(true);
    try {
      const r = await api.post("/ai/evaluate-writing", { exam_type: examType, task_type: taskType, prompt, answer });
      setResult(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Eval failed"); }
    finally { setLoading(false); }
  };

  const wordCount = answer.trim().split(/\s+/).filter(Boolean).length;

  return (
    <DashboardLayout title="AI Writing Evaluator">
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-3xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <select value={examType} onChange={(e) => setExamType(e.target.value)} data-testid="we-exam" className="bg-[#F6F8FB]/50 border border-slate-200 rounded-xl px-3 py-2 text-sm">
              <option value="IELTS_ACADEMIC">IELTS Academic</option>
              <option value="IELTS_GENERAL">IELTS General</option>
              <option value="PTE">PTE</option>
              <option value="OXFORD_ELLT">Oxford ELLT</option>
              <option value="DUOLINGO">Duolingo</option>
            </select>
            <select value={taskType} onChange={(e) => setTaskType(e.target.value)} data-testid="we-task" className="bg-[#F6F8FB]/50 border border-slate-200 rounded-xl px-3 py-2 text-sm">
              <option value="TASK1">Task 1</option>
              <option value="TASK2">Task 2</option>
              <option value="ESSAY">Essay</option>
            </select>
          </div>
          <div className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2">Prompt</div>
          <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={4} data-testid="we-prompt"
            className="w-full bg-[#F6F8FB]/50 border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:border-[#F77F00] text-sm" />
          <div className="text-xs font-mono uppercase tracking-widest text-slate-400 mt-5 mb-2 flex items-center justify-between">
            <span>Your answer</span><span>{wordCount} words</span>
          </div>
          <textarea value={answer} onChange={(e) => setAnswer(e.target.value)} rows={14} data-testid="we-answer"
            placeholder="Start writing your essay..."
            className="w-full bg-[#F6F8FB]/50 border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:border-[#F77F00]" />
          <button onClick={submit} disabled={loading} data-testid="we-submit-btn" className="mt-4 w-full bg-[#F77F00] text-white font-medium rounded-xl py-3 hover:bg-slate-100/90 disabled:opacity-50 inline-flex items-center justify-center gap-2">
            {loading ? "Analysing..." : (<><Sparkles className="w-4 h-4" /> Evaluate</>)}
          </button>
        </div>

        <div className="bg-white border border-slate-200 rounded-3xl p-6">
          {!result ? (
            <div className="h-full flex flex-col items-center justify-center text-center text-slate-400 py-16">
              <Sparkles className="w-10 h-10 mb-4 text-[#0047FF]" />
              <div className="font-display text-2xl text-slate-900">AI feedback will appear here</div>
              <div className="text-sm mt-2 max-w-xs">Band-by-band scoring across all official criteria with rewrite tips.</div>
            </div>
          ) : (
            <div data-testid="we-result">
              <div className="text-xs font-mono uppercase tracking-widest text-slate-400">Predicted Score</div>
              <div className="font-display text-7xl mt-2">{result.overall_band}</div>
              <div className="mt-6 space-y-3">
                {Object.entries(result.criteria || {}).map(([k, v]) => (
                  <div key={k} className="rounded-xl border border-slate-200 p-4">
                    <div className="flex items-center justify-between">
                      <div className="text-sm capitalize">{k.replaceAll("_", " ")}</div>
                      <div className="font-mono">{v.score ?? "—"}</div>
                    </div>
                    <div className="text-xs text-slate-500 mt-1.5">{v.comment}</div>
                  </div>
                ))}
              </div>
              <div className="mt-6">
                <div className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2">Feedback</div>
                <p className="text-sm text-slate-700 leading-relaxed">{result.feedback}</p>
              </div>
              {result.suggestions?.length > 0 && (
                <div className="mt-6">
                  <div className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2">Quick wins</div>
                  <ul className="space-y-2">
                    {result.suggestions.map((s, i) => <li key={i} className="text-sm text-slate-700 flex gap-2"><span className="text-[#0047FF]">›</span>{s}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <AICoachFloating />
    </DashboardLayout>
  );
}
